import { Route } from '@angular/router';
import { SettingsComponent } from './settings.component';
import { UserSettingsComponent } from './users/users.component';
import { GeneralSettingsComponent } from './general/general.component';
import { SamlSettingsComponent } from './saml/saml.component';
import { ProjectDataResolver } from 'app/modules/projects/projects.resolvers';
import { ContactsCategoriesResolver } from 'app/modules/contacts/contacts.resolvers';
import { CustomizationsSettingsComponent } from './customizations/customizations.component';
import {
  SettingsUsersResolver,
  SettingsInvitationsResolver,
  SettingsRolesResolver,
} from 'app/modules/settings/settings.resolvers';
import { SettingsGuard } from './settings.guard';
import { AuthorizationGuard } from 'app/core/guards/authorization.guard';
import { FeatureFlag, Module, NewPermission, Permission } from 'app/core/enums';
import { RelatedToType } from '@shared/enums';
import { FeatureFlagGuard } from '@core/guards';
import { AccountService } from '@core/services';
import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';

export const settingsRoutes: Route[] = [
  {
    path: '',
    component: SettingsComponent,
    canActivate: [SettingsGuard],
    children: [
      {
        path: '',
        redirectTo: 'general',
        pathMatch: 'full',
      },
      {
        path: 'general',
        data: { module: Module.SETTINGS },
        canActivate: [AuthorizationGuard],
        component: GeneralSettingsComponent,
      },
      {
        path: 'users',
        data: { permission: Permission.SETTINGS_USERS },
        canActivate: [AuthorizationGuard],
        component: UserSettingsComponent,
        resolve: {
          users: SettingsUsersResolver,
          invitations: SettingsInvitationsResolver,
          options: ProjectDataResolver,
          roles: SettingsRolesResolver,
        },
      },
      {
        path: 'customizations',
        data: { permission: Permission.SETTINGS_DROPDOWNS },
        canActivate: [
          AuthorizationGuard,
          (): Observable<boolean> => {
            const accountSvc = inject(AccountService);
            accountSvc.bustStandardDropdownsCache();
            return accountSvc.getStandardDropdowns().pipe(map(() => true));
          },
        ],
        component: CustomizationsSettingsComponent,
        resolve: {
          options: ProjectDataResolver,
          categories: ContactsCategoriesResolver,
        },
      },
      {
        path: 'teams',
        data: {
          permission: NewPermission.SETTINGS_MANAGE_TEAMS,
          featureFlag: FeatureFlag.NEW_PERMISSIONS,
          redirectPath: 'settings/users',
        },
        canActivate: [AuthorizationGuard, FeatureFlagGuard],
        loadComponent: () => import('./teams/teams.component').then((m) => m.TeamsSettingsComponent),
      },
      {
        path: 'roles',
        data: { permission: Permission.SETTINGS_PERMISSIONS },
        canActivate: [AuthorizationGuard],
        loadChildren: () =>
          import('./roles-permissions/roles-permissions.module').then((m) => m.RolesPermissionsModule),
      },
      {
        path: 'chart-of-accounts',
        canActivate: [AuthorizationGuard, FeatureFlagGuard],
        data: { featureFlag: FeatureFlag.ASSET_MANAGEMENT, redirectPath: 'settings' },
        loadComponent: () =>
          import(
            '../asset-management/containers/asset-management-global-settings-container/asset-management-global-settings-container.component'
          ).then((module) => module.AssetManagementGlobalSettingsContainerComponent),
      },
      {
        path: 'accounts-mapping',
        canActivate: [AuthorizationGuard, FeatureFlagGuard],
        data: { featureFlag: FeatureFlag.ASSET_MANAGEMENT, redirectPath: 'settings' },
        loadComponent: () =>
          import('../asset-management/containers/account-mapping-container/account-mapping-container.component').then(
            (module) => module.AccountMappingContainerComponent,
          ),
      },
      {
        path: 'custom_fields',
        data: { permission: Permission.SETTINGS_FIELDS },
        canActivate: [AuthorizationGuard],
        resolve: {
          options: ProjectDataResolver,
        },
        loadChildren: () => import('./locked-fields/locked-fields.module').then((m) => m.LockedFieldsModule),
      },
      {
        path: 'objects_manager',
        data: { permission: Permission.SETTINGS_OBJECT_MANAGER },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./objects-manager/objects-manager.module').then((m) => m.ObjectsManagerModule),
      },
      {
        path: 'imports',
        data: { permission: Permission.SETTINGS_IMPORTS },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./imports/imports.module').then((m) => m.ImportsModule),
      },
      {
        path: 'automations',
        data: { permission: Permission.SETTINGS_AUTOMATIONS },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./automations/automations.module').then((m) => m.AutomationsModule),
      },
      {
        path: 'integrations',
        data: { permission: Permission.SETTINGS_APPS },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./integrations/integrations.module').then((m) => m.IntegrationsModule),
      },
      {
        path: 'sheets',
        data: { permission: Permission.SETTINGS_SHEETS },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./sheet-templates/sheet-templates.module').then((m) => m.SheetTemplatesModule),
      },
      {
        path: 'functions',
        data: { permission: Permission.SETTINGS_CODE },
        canActivate: [AuthorizationGuard],
        loadChildren: () =>
          import('./account-functions/account-functions.module').then((m) => m.AccountFunctionsModule),
      },
      {
        path: 'hooks',
        data: { permission: Permission.SETTINGS_CODE },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./account-hooks/account-hooks.module').then((m) => m.AccounHooksModule),
      },
      {
        path: 'task-lists',
        data: { permission: Permission.SETTINGS_TASK_TEMPLATES, taskModuleRelatedTo: RelatedToType.Account },
        canActivate: [AuthorizationGuard],
        loadChildren: () =>
          import('./task-list-templates/task-list-templates.module').then((m) => m.TaskListTemplatesModule),
      },
      {
        path: 'task-roles',
        data: { permission: Permission.SETTINGS_TASK_ROLES },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./task-roles/task-roles.module').then((m) => m.TaskRolesModule),
      },
      {
        path: 'layouts',
        data: { permission: Permission.SETTINGS_TASK_TEMPLATES },
        canActivate: [AuthorizationGuard],
        loadChildren: () => import('./layouts/layouts.module').then((m) => m.SettingsLayoutsModule),
      },
      {
        path: 'audit',
        data: { permission: NewPermission.SETTINGS_MANAGE_AUDIT },
        canActivate: [AuthorizationGuard],
        loadComponent: () => import('./audit/audit.component').then((m) => m.AuditSettingsComponent),
      },
      {
        path: 'saml',
        data: { permission: Permission.SETTINGS_FIELDS },
        canActivate: [AuthorizationGuard],
        component: SamlSettingsComponent,
      },
    ],
  },
];
